import { fetchEventSource } from "@microsoft/fetch-event-source"

import type { FetchEventSourceInit } from "@microsoft/fetch-event-source"

class FatalError extends Error {}
class RetryableError extends Error {}

// Retry backoff parameters.
// TODO: consider making this configurable since this concerns API requests made to
// the integrator's server.
const BASE_RETRY_DELAY_MS = 1000
const MAX_RETRY_DELAY_MS = 10_000
const MAX_RETRY_ATTEMPTS = 5

// Backoff with half jitter.
// With the default parameters, results in the following retry delays:
// 1st: 1–2s, 2nd: 2–4s, 3rd: 4–8s, 4th+: 5–10s (capped).
const nextDelay = (attempt: number) => {
  const max = Math.min(MAX_RETRY_DELAY_MS, BASE_RETRY_DELAY_MS * 2 ** attempt)
  // uniform in [0.5*max, max)
  return Math.floor(max * (0.5 + Math.random() * 0.5))
}

export const fetchEventSourceWithRetry = async (
  url: string,
  params: Omit<FetchEventSourceInit, "onopen" | "onerror" | "onclose">,
) => {
  let retryAttempt = 0
  await fetchEventSource(url, {
    ...params,
    onopen: async (res) => {
      if (res.ok && res.headers.get("content-type")?.startsWith("text/event-stream")) {
        // All is well, we can continue streaming.
        retryAttempt = 0 // reset on success
        return
      } else if (res.status >= 400 && res.status < 500 && res.status !== 429) {
        // This is likely a fatal error, client's are supposed to send errors as SSE
        // but we can handle this gracefully by ending the streaming state. It could be
        // that this error is due to a misconfigured server or an upstream proxy returning
        // an error, etc.
        throw new FatalError(`Streaming failed: ${res.statusText}`)
      } else {
        // A 5xx error, we can let the client retry.
        throw new RetryableError()
      }
    },
    onerror: (error) => {
      if (error instanceof RetryableError) {
        if (retryAttempt >= MAX_RETRY_ATTEMPTS) {
          throw new FatalError("Max retry attempts reached")
        }

        retryAttempt += 1
        return nextDelay(retryAttempt) // returning a number sets the retry wait
      }

      // All other errors are fatal and should not be retried.
      // TODO: log error for monitoring purposes
      throw error
    },
  })
}
